import React, { useState, useEffect } from "react";
import axiosClient from "../utils/apiClient.js";
import { useParams, Link } from "react-router-dom";
import { BiPlus, BiPencil, BiTrash, BiSearch, BiDollarCircle } from "react-icons/bi";
import { toast } from "react-toastify";

const InvoiceList = () => {
  const { clientId } = useParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));

  const canManageInvoices =
    user?.roleName === "admin" || user?.roleName === "case_manager";
  const isClient = user?.roleName === "client";

  useEffect(() => {
    setSelectedIds([]);
  }, [searchQuery, clientId]);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        let response;

        if (clientId) {
          response = await axiosClient.get(`/invoices?client_id=${clientId}`);
          setInvoices(response.data.invoices || []);
        } else {
          response = await axiosClient.get(`/invoices`);
          setInvoices(response.data.invoices || []);
        }
      } catch (err) {
        console.error("Error fetching invoices:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load invoices. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [clientId]);

  const handleToggleSelect = (invoiceId) => {
    setSelectedIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredInvoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredInvoices.map((inv) => inv.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected invoice(s)?`
      )
    )
      return;

    setIsDeleting(true);
    try {
      await axiosClient.post("/invoices/bulk-delete", {
        invoiceIds: selectedIds,
      });
      setInvoices((prev) =>
        prev.filter((inv) => !selectedIds.includes(inv.id))
      );
      toast.success(
        `Successfully deleted ${selectedIds.length} invoice(s).`
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete error, trying fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) => axiosClient.delete(`/invoices/${id}`))
        );
        setInvoices((prev) =>
          prev.filter((inv) => !selectedIds.includes(inv.id))
        );
        toast.success(
          `Successfully deleted ${selectedIds.length} invoice(s).`
        );
        setSelectedIds([]);
      } catch (fallbackErr) {
        console.error("Fallback delete error:", fallbackErr);
        toast.error(
          err.response?.data?.message || "Failed to delete selected invoices."
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;

    try {
      await axiosClient.delete(`/invoices/${invoiceId}`);
      setInvoices((prev) => prev.filter((inv) => inv.id !== invoiceId));
      setSelectedIds((prev) => prev.filter((id) => id !== invoiceId));
      toast.success("Invoice deleted successfully.");
    } catch (err) {
      console.error("Delete invoice error:", err);
      toast.error(err.response?.data?.message || "Failed to delete invoice.");
    }
  };


  // Searching
  const filteredInvoices = invoices.filter((invoice) => {
    const fullName =
      `${invoice.client_first || ""} ${invoice.client_last || ""}`.toLowerCase();

    return (
      invoice.invoice_number.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fullName.includes(searchQuery.toLowerCase())
    );
  });

  const totalInvoices = invoices.length;
  const paidInvoices = invoices.filter((inv) => inv.status === "paid").length;

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "unpaid":
        return "bg-red-100 text-red-800";
      case "payment_failed":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatLabel = (value) => {
    if (!value) return "";
    return value
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getLinkPath = (invoice) => {
    if (isClient) return `/invoices/client/${clientId}/${invoice.id}`;
    return `/invoices/${invoice.id}`;
  };

  const InvoiceCard = ({ invoice }) => (
    <div className={`bg-white p-4 rounded-lg shadow-sm border mb-3 ${selectedIds.includes(invoice.id) ? "border-blue-500 bg-blue-50/30" : "border-gray-100"}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center space-x-2">
          {canManageInvoices && (
            <input
              type="checkbox"
              checked={selectedIds.includes(invoice.id)}
              onChange={() => handleToggleSelect(invoice.id)}
              aria-label={`Select invoice ${invoice.invoice_number}`}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer mr-1"
            />
          )}
          <BiDollarCircle className="text-blue-600 h-5 w-5" />
          <h2 className="text-lg font-semibold">Invoice #{invoice.invoice_number}</h2>
        </div>
        <span
          className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
            invoice.status
          )}`}
        >
          {formatLabel(invoice.status)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-600 border-t pt-2">
        {!clientId && (
          <div className="col-span-2">
            <span className="font-medium">Client: </span>
            {invoice.client_first} {invoice.client_last}
          </div>
        )}
        <div>
          <span className="font-medium">Amount: </span>
          <span className="font-bold">${parseFloat(invoice.total_amount).toFixed(2)}</span>
        </div>
        <div>
          <span className="font-medium">Due: </span>
          {new Date(invoice.due_date).toLocaleDateString()}
        </div>
        <div className="col-span-2">
          <span className="font-medium">Issued: </span>
          {new Date(invoice.issue_date).toLocaleDateString()}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t flex justify-end space-x-3">
        <Link
          to={getLinkPath(invoice)}
          className="text-sm px-3 py-1 border rounded text-blue-600"
        >
          View Details
        </Link>

        {canManageInvoices && (
          <>
            <Link to={`/invoices/edit/${invoice.id}`} className="text-indigo-600 p-1">
              <BiPencil className="h-5 w-5" />
            </Link>

            <button onClick={() => handleDelete(invoice.id)} className="text-red-600 p-1">
              <BiTrash className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (loading) return <div className="p-4 text-center">Loading invoices...</div>;
  if (error) return <div className="p-4 text-center text-red-500">{error}</div>;

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h1 className="text-2xl font-bold">Invoices</h1>

        <div className="flex flex-col sm:flex-row sm:space-x-4 w-full sm:w-auto">
          <div className="relative w-full sm:w-64 mb-3 sm:mb-0">
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-full"
            />
            <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          {canManageInvoices && (
            <Link
              to="/invoices/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center"
            >
              <BiPlus className="mr-2" />
              New Invoice
            </Link>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded shadow-sm border-l-4 border-blue-500">
          <p className="text-gray-500 text-sm">Total Invoices</p>
          <p className="text-xl font-semibold">{totalInvoices}</p>
        </div>
        <div className="bg-white p-4 rounded shadow-sm border-l-4 border-green-500">
          <p className="text-gray-500 text-sm">Paid</p>
          <p className="text-xl font-semibold">{paidInvoices}</p>
        </div>
        <div className="bg-white p-4 rounded shadow-sm border-l-4 border-red-500">
          <p className="text-gray-500 text-sm">Unpaid</p>
          <p className="text-xl font-semibold">{totalInvoices - paidInvoices}</p>
        </div>
        <div className="bg-white p-4 rounded shadow-sm border-l-4 border-yellow-500 hidden md:block">
          <p className="text-gray-500 text-sm">Failed</p>
          <p className="text-xl font-semibold">
            {invoices.filter((inv) => inv.status === "payment_failed").length}
          </p>
        </div>
      </div>

      {/* Bulk actions banner */}
      {canManageInvoices && selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>{selectedIds.length} of {filteredInvoices.length} selected</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition disabled:opacity-50"
          >
            <BiTrash className="mr-1 h-4 w-4" />
            {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
          </button>
        </div>
      )}

      {/* List */}
      {filteredInvoices.length > 0 ? (
        <>
          {/* Mobile cards */}
          <div className="md:hidden">
            {filteredInvoices.map((invoice) => (
              <InvoiceCard key={invoice.id} invoice={invoice} />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  {canManageInvoices && (
                    <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={filteredInvoices.length > 0 && selectedIds.length === filteredInvoices.length}
                        onChange={handleSelectAll}
                        aria-label="Select all invoices"
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium">Invoice #</th>
                  {!clientId && <th className="px-6 py-3 text-left text-xs font-medium">Client</th>}
                  <th className="px-6 py-3 text-left text-xs font-medium">Issue Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium">Status</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedIds.includes(invoice.id) ? "bg-blue-50/50" : ""
                    }`}
                  >
                    {canManageInvoices && (
                      <td className="px-4 py-4 text-sm w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(invoice.id)}
                          onChange={() => handleToggleSelect(invoice.id)}
                          aria-label={`Select invoice ${invoice.invoice_number}`}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4" style={{ fontSize: '0.875rem' }}>{invoice.invoice_number}</td>


                    {!clientId && (
                      <td className="px-6 py-4" style={{ fontSize: '0.875rem' }}>
                        {invoice.agency ? `${ invoice.agency +" ("+ invoice.client_first + " "  + invoice.client_last+")"}` : `${invoice.client_first + " "  + invoice.client_last}`}
                      </td>
                    )}
                    <td className="px-6 py-4" style={{ fontSize: '0.875rem' }}>
                      {new Date(invoice.issue_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4" style={{ fontSize: '0.875rem' }}>
                      {new Date(invoice.due_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 font-semibold" style={{ fontSize: '0.875rem' }} >
                      ${parseFloat(invoice.total_amount).toFixed(2)}
                    </td>

                    <td className="px-6 py-4" style={{ fontSize: '0.875rem' }}>
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-semibold ${getStatusColor(
                          invoice.status
                        )}`}
                      >
                        {formatLabel(invoice.status)}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <Link to={getLinkPath(invoice)} className="text-blue-600 mr-3">
                        View
                      </Link>

                      {canManageInvoices && (
                        <>
                          <Link
                            to={`/invoices/edit/${invoice.id}`}
                            className="text-indigo-600 mr-3"
                          >
                            <BiPencil className="inline-block h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => handleDelete(invoice.id)}
                            className="text-red-600"
                          >
                            <BiTrash className="inline-block h-5 w-5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="p-6 bg-white rounded shadow-sm text-center text-gray-500">
          No invoices found.
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
